import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { CustomButton, Text } from "../../../shared/uiComponents";
import { errorNormalizer } from "../../../shared/Helpers/functions";
import { Wrapper } from "../../../components/StyledComponents";
import SessionData from "./sessionData";
import SessionPrograms from "./sessionPrograms";
import Behaviours from "./ClientBehaviors/behaviours";
import HiddenTargets from "./hiddenTargets";

import { DispatchProperties, useSelector } from "../../../redux/store";
import { getPromptCodes } from "../../../redux/State/clientSlice/dataCollectionSlice";
import {
  clearSessionInfo,
  getAllActiveClientTargets,
  getSessionInfo,
  getSessionTargets,
} from "../../../redux/State/clientSlice/sessionSlice";
import {
  clearErrors,
  createSoapNote,
} from "../../../redux/State/clientSlice/soapNoteSlice";
import { setInfoEvent } from "../../../redux/State/clientSlice/calendarSlice";

const Session = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();
  const { clientId, sessionId } = useParams();

  const promptsData = useSelector((state) => state.dataCollection.promptCodes);
  const data = useSelector((state) => state.session.sessionInfo);

  useEffect(() => {
    if (!sessionId) return;
    dispatch(getSessionInfo(sessionId))
  }, [sessionId, dispatch])

  useEffect(() => {
    if (!!promptsData.length) return;
    dispatch(getPromptCodes());
  }, [promptsData.length, dispatch]);

  const fetchActiveTargets = useCallback(() => {
    if (!sessionId) return;
    dispatch(getSessionTargets(sessionId));
  }, [sessionId, dispatch]);

  useEffect(() => {
    if (!clientId) return;
    dispatch(getAllActiveClientTargets({ clientId }))
      .then(unwrapResult)
      .then(() => {
        fetchActiveTargets();
      })
      .catch(errorNormalizer);
  }, [clientId, dispatch, fetchActiveTargets]);

  useEffect(() => {
    let start = new Date();
    const interval = setInterval(() => {
      const now = new Date();
      const diff = Math.floor((now.getTime() - start.getTime()) / 60000);
      if (diff > 30) {
        start = new Date(start.setHours(start.getHours() + 1));
        toast(
          "Please remember to have at least three targets and behaviors per hours!"
        );
      }
    }, 900000);

    return () => clearInterval(interval);
  }, []);

  const navigateEditSoapNote = ({
    clientId,
    reportId,
    sessionId,
  }: {
    clientId: string;
    reportId: string;
    sessionId: string;
  }) => {
    dispatch(setInfoEvent(false));
    dispatch(clearSessionInfo());
    dispatch(clearErrors());

    navigate(
      `/soap-notes/info?clientId=${clientId}&reportId=${reportId}&sessionId=${sessionId}`
    );
  };

  const navigateToSoapNoteHandler = () => {
    if (!clientId || !sessionId) return;

    const reportId = data?.reportId;

    if (!reportId) {
      dispatch(createSoapNote({ clientId, sessionId }))
        .then(unwrapResult)
        .then(({ reportId }) => {
          if (!reportId) return;
          navigateEditSoapNote({ clientId, reportId, sessionId });
        })
        .catch(errorNormalizer);
      return;
    }
    navigateEditSoapNote({ clientId, reportId, sessionId });
  };

  return (
    <>
      <Wrapper style={{ maxWidth: "760px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {!!data?.id && (
            <div>
              <div style={{ display: "flex", gap: "10px" }}>
                <Text title={"Date: "} size="smallBold" />
                <Text title={dayjs(data.date).format("MM/DD/YYYY")} />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <Text title={"Start time: "} size="smallBold" />
                <Text
                  title={dayjs(data.startTime).format("hh:mm A")}
                />
              </div>
            </div>
          )}
          <HiddenTargets />
        </div>
      </Wrapper>
      <SessionData />
      <SessionPrograms />
      <Behaviours />
      <div style={{ maxWidth: "760px" }}>
        <CustomButton
          title="Start SOAP Note"
          onClick={navigateToSoapNoteHandler}
          className="marginTop16"
        />
      </div>
    </>
  );
};

export default Session;
